/**
 * A meteor in the yard: where it is and how it looks. The rules (health,
 * countdown, shards) live in game/meteors.ts; this module runs the fall,
 * the bars, the hits and the two endings on the canvas. It is driven by
 * nest.ts, which owns the canvas, the particles and the herd.
 *
 * Life cycle: waiting (a shower's later rocks hold off so they do not all
 * land at once), incoming (a fireball streaks down onto the dirt), sitting
 * (a rock with a health bar and the seconds left over it; clicks chip it), then
 * either broken (it bursts and the shards float up) or cooled (it darkens
 * and sinks).
 */

import { TICKS_PER_SECOND, TICK_SECONDS } from '../game/loop';
import { hitMeteor, tickMeteor } from '../game/meteors';
import type { Meteor, MeteorStatus } from '../game/meteors';
import { meteorSprite, silhouetteOf } from './sprites';

// Palette (Endesga 32)
const ROCK = '#8b9bb4';
const ROCK_SHADE = '#5a6988';
const ROCK_DARK = '#3a4466';
const ROCK_COOL = '#262b44';
const OUTLINE = '#181425';
const FLAME = '#f77622';
const FLAME_HOT = '#feae34';
const FLAME_TIP = '#fee761';
const HEALTH = '#e43b44';
const HEALTH_BACK = '#3e2731';
const WHITE = '#ffffff';
const HURRY = '#feae34';

/**
 * The seconds left, in screen pixels. Set in the page's own face and drawn
 * at the canvas's full resolution rather than as pixel art, so it reads as
 * cleanly as the text around the yard.
 */
const COUNTDOWN_PX = 18;
/** The figure turns yellow at this many seconds left, and red at the next. */
const HURRY_SECONDS = 5;
const LAST_SECONDS = 3;

const INCOMING_TICKS = TICKS_PER_SECOND;
const ENDING_TICKS = 10;
/** Extra pixels around the rock that still count as a hit. */
const HIT_SLOP = 4;
/** How far in front of the horizon the rock lands (rows below groundY), a random depth in this range. */
const LAND_ROWS_MIN = 30;
/** Deepest landing row: clear of a 24-row stream along the bottom (nest.ts WATER_ROWS). */
const LAND_ROWS_MAX = 66;
/** No landing within this many pixels of the nest's centre. */
const NEST_CLEARANCE = 72;
const EDGE_MARGIN = 12;
/** Rows apart two rocks of a shower must be to share a column. */
const SHOWER_ROW_GAP = 18;
/** Ticks between one rock of a shower arriving and the next. */
export const SHOWER_STAGGER_TICKS = 5;

/** Rock sprite size per kind id (public/art/meteors/<id>.png); anything unknown is medium. */
const SIZES: Readonly<Record<string, number>> = {
  pebble: 16,
  stone: 20,
  iron: 24,
  core: 28,
  nova: 32,
};
const FIREBALL_SIZE = 24;

type Phase = 'incoming' | 'sitting' | 'ending';

export interface YardMeteor {
  readonly meteor: Meteor;
  /** Where the rock is (or will land): centre x, bottom y. */
  x: number;
  y: number;
  size: number;
  phase: Phase;
  /** Ticks into the current phase. */
  age: number;
  /** Ticks to hold before the fall starts, so a shower arrives strung out. */
  delay: number;
}

export interface MeteorSceneHooks {
  /** Damage this click does (the player's tool). */
  damage(): number;
  /** A fireball hit the dirt. */
  onImpact(x: number, y: number): void;
  /** A click chipped the rock. */
  onHit(x: number, y: number): void;
  /** The rock burst; returns the shards paid so the float can show them. */
  onBroken(m: YardMeteor, x: number, y: number): number;
  onCooled(m: YardMeteor): void;
}

export interface MeteorLayout {
  width: number;
  groundY: number;
  eggCx: number;
  /**
   * The lowest row a rock may rest on, when the stream is taller than usual
   * (the Ice Age's shelf ice): rocks are drawn under the water, so one lower
   * than this would be hidden.
   */
  floorY?: number;
}

/**
 * Whether a candidate spot would crowd a rock already placed. The bars are
 * wider than the rock they belong to, so they are what has to clear: two
 * pebbles far enough apart not to touch can still have overlapping bars.
 */
function overlaps(x: number, y: number, size: number, placed: readonly YardMeteor[]): boolean {
  return placed.some(
    (p) =>
      Math.abs(p.x - x) < (barWidth(p.size) + barWidth(size)) / 2 + 2 &&
      Math.abs(p.y - y) < SHOWER_ROW_GAP,
  );
}

export function createYardMeteor(
  meteor: Meteor,
  layout: MeteorLayout,
  random: () => number = Math.random,
  /** Rocks already in the yard, so a shower does not stack on itself. */
  placed: readonly YardMeteor[] = [],
  delay = 0,
): YardMeteor {
  const size = SIZES[meteor.kind.id] ?? 18;
  // A landing site along the dirt, clear of the nest, the edges and the rest
  // of the shower. If no try is clear (a narrow phone yard has almost no
  // dirt beyond the nest's clearance), the best one is taken: off the other
  // rocks first, then as far from the nest as it got. Taking the last try
  // instead piled rocks behind the egg, where they took its clicks.
  const half = size / 2;
  let x = 0;
  let y = 0;
  let bestScore = -Infinity;
  const deepest = Math.min(LAND_ROWS_MAX, (layout.floorY ?? Infinity) - layout.groundY);
  const depthRange = Math.max(0, deepest - LAND_ROWS_MIN);
  for (let tries = 0; tries < 30; tries++) {
    const tx = EDGE_MARGIN + half + random() * (layout.width - 2 * (EDGE_MARGIN + half));
    const ty = layout.groundY + LAND_ROWS_MIN + Math.round(random() * depthRange);
    const fromNest = Math.abs(tx - layout.eggCx);
    const clearOfRocks = !overlaps(tx, ty, size, placed);
    const score = (clearOfRocks ? layout.width : 0) + fromNest;
    if (score > bestScore) {
      bestScore = score;
      x = tx;
      y = ty;
    }
    if (clearOfRocks && fromNest >= NEST_CLEARANCE) break;
  }
  return {
    meteor,
    x: Math.round(x),
    y,
    size,
    phase: 'incoming',
    age: 0,
    delay,
  };
}

/** The rock's box on the canvas (its resting spot; the fall is drawn elsewhere). */
function rockBox(m: YardMeteor): { x: number; y: number; w: number; h: number } {
  return { x: m.x - m.size / 2, y: m.y - m.size, w: m.size, h: m.size };
}

/** Whether a native-pixel point is on the rock (with a little slop) while it can be hit. */
export function isOnMeteor(m: YardMeteor, px: number, py: number): boolean {
  if (m.phase !== 'sitting' || m.delay > 0) return false;
  const b = rockBox(m);
  return (
    px >= b.x - HIT_SLOP &&
    px < b.x + b.w + HIT_SLOP &&
    py >= b.y - HIT_SLOP &&
    py < b.y + b.h + HIT_SLOP
  );
}

/** A click on the rock. Returns the status afterwards. */
export function clickMeteor(m: YardMeteor, hooks: MeteorSceneHooks): MeteorStatus {
  if (m.phase !== 'sitting') return m.meteor.status;
  const status = hitMeteor(m.meteor, hooks.damage());
  hooks.onHit(m.x, m.y - m.size / 2);
  if (status === 'broken') {
    m.phase = 'ending';
    m.age = 0;
    hooks.onBroken(m, m.x, m.y - m.size / 2);
  }
  return status;
}

/**
 * One logic tick. Returns true while the meteor is still on the canvas;
 * false once its ending has played out and it should be dropped.
 */
export function tickYardMeteor(m: YardMeteor, hooks: MeteorSceneHooks): boolean {
  if (m.delay > 0) {
    m.delay--;
    return true;
  }
  m.age++;
  switch (m.phase) {
    case 'incoming':
      if (m.age >= INCOMING_TICKS) {
        m.phase = 'sitting';
        m.age = 0;
        hooks.onImpact(m.x, m.y);
      }
      return true;
    case 'sitting':
      if (tickMeteor(m.meteor, TICK_SECONDS) === 'cooled') {
        m.phase = 'ending';
        m.age = 0;
        hooks.onCooled(m);
      }
      return true;
    case 'ending':
      return m.age < ENDING_TICKS;
  }
}

/** Where the fireball is during the fall, for the ember trail. */
export function fireballPosition(m: YardMeteor): { x: number; y: number } {
  const t = Math.min(1, m.age / INCOMING_TICKS);
  // Comes in from above and slightly to the side, easing in.
  const startX = m.x + 40;
  const startY = -m.size;
  return { x: startX + (m.x - startX) * t, y: startY + (m.y - startY) * t * t };
}

/**
 * Draws the meteor for its current phase; procedural stand-ins until the art
 * loads. `scale` is screen pixels per native pixel, which the countdown needs
 * to be set at a fixed screen size.
 */
export function drawYardMeteor(ctx: CanvasRenderingContext2D, m: YardMeteor, scale: number): void {
  if (m.delay > 0) return; // Not in the sky yet.
  if (m.phase === 'incoming') {
    const { x, y } = fireballPosition(m);
    const sprite = meteorSprite('fireball');
    if (sprite) {
      ctx.drawImage(sprite, Math.round(x - FIREBALL_SIZE / 2), Math.round(y - FIREBALL_SIZE / 2));
    } else {
      drawFireball(ctx, Math.round(x), Math.round(y), m.size);
    }
    return;
  }
  if (m.phase === 'ending') {
    if (m.meteor.status === 'cooled') {
      // Darkens and sinks into the dirt.
      const sink = Math.round((m.age / ENDING_TICKS) * m.size);
      const sprite = meteorSprite(m.meteor.kind.id);
      if (sprite) {
        const b = rockBox(m);
        const h = b.h - sink;
        if (h > 0) {
          ctx.drawImage(sprite, 0, 0, b.w, h, b.x, b.y + sink, b.w, h);
          // The one alpha use here: a dark wash as it cools, in the rock's
          // own shape. A plain rectangle darkened the corners of the sprite's
          // box too, and showed as a dark square round the rock.
          ctx.globalAlpha = 0.6 * (m.age / ENDING_TICKS);
          ctx.drawImage(silhouetteOf(sprite, OUTLINE), 0, 0, b.w, h, b.x, b.y + sink, b.w, h);
          ctx.globalAlpha = 1;
        }
      } else {
        drawRock(ctx, m, ROCK_COOL, ROCK_DARK, sink);
      }
    }
    // A broken one is just its fragments (particles), drawn by the scene.
    return;
  }
  const sprite = meteorSprite(m.meteor.kind.id);
  if (sprite) {
    const b = rockBox(m);
    ctx.drawImage(sprite, b.x, b.y);
    drawCracks(ctx, m);
  } else {
    drawRock(ctx, m, ROCK, ROCK_SHADE, 0);
  }
  drawBars(ctx, m, scale);
}

/** Crack lines over the rock once it is below half health. */
function drawCracks(ctx: CanvasRenderingContext2D, m: YardMeteor): void {
  if (m.meteor.health > m.meteor.kind.health / 2) return;
  const b = rockBox(m);
  const cx = Math.round(b.x + b.w / 2);
  const top = b.y;
  const h = b.h;
  ctx.fillStyle = OUTLINE;
  ctx.fillRect(cx, top + 3, 1, Math.round(h / 3));
  ctx.fillRect(cx - 2, top + Math.round(h / 3), 2, 1);
  ctx.fillRect(cx - 2, top + Math.round(h / 3), 1, Math.round(h / 4));
  ctx.fillRect(cx + 1, top + Math.round(h / 2), 3, 1);
  ctx.fillRect(cx + 3, top + Math.round(h / 2), 1, Math.round(h / 5));
}

function drawRock(
  ctx: CanvasRenderingContext2D,
  m: YardMeteor,
  fill: string,
  shade: string,
  sink: number,
): void {
  const b = rockBox(m);
  const top = b.y + sink;
  const h = b.h - sink;
  if (h <= 0) return;
  // A lumpy oval: rows of varying half-width, outlined.
  const cx = b.x + b.w / 2;
  for (let row = 0; row < h; row++) {
    const t = (row + 0.5) / b.h;
    const halfW = Math.round(
      (b.w / 2) * Math.sqrt(1 - (2 * t - 1) ** 2) + (row % 3 === 0 ? 0.5 : 0),
    );
    const y = top + row;
    ctx.fillStyle = OUTLINE;
    ctx.fillRect(Math.round(cx - halfW) - 1, y, halfW * 2 + 2, 1);
    ctx.fillStyle = row > h * 0.55 ? shade : fill;
    ctx.fillRect(Math.round(cx - halfW), y, halfW * 2, 1);
  }
  ctx.fillStyle = OUTLINE;
  ctx.fillRect(Math.round(cx - b.w / 4), top - 1, Math.round(b.w / 2), 1);
  ctx.fillRect(Math.round(cx - b.w / 4), top + h, Math.round(b.w / 2), 1);
  // Cracks once below half health.
  if (m.meteor.health <= m.meteor.kind.health / 2 && m.meteor.status === 'sitting') {
    ctx.fillStyle = OUTLINE;
    ctx.fillRect(Math.round(cx), top + 2, 1, Math.round(h / 3));
    ctx.fillRect(Math.round(cx) - 2, top + Math.round(h / 3), 2, 1);
    ctx.fillRect(Math.round(cx) - 2, top + Math.round(h / 3), 1, Math.round(h / 4));
    ctx.fillRect(Math.round(cx) + 1, top + Math.round(h / 2), 3, 1);
  }
  // A highlight.
  ctx.fillStyle = WHITE;
  ctx.fillRect(Math.round(cx - b.w / 4), top + 2, 2, 1);
}

/** The health bar is wider than the rock, and sets the spacing. */
function barWidth(size: number): number {
  return Math.max(size + 8, 20);
}

/**
 * The health bar, and the seconds left written over it. The time was a second
 * bar under the health one; two bars the same length read as one measure, and
 * the one the player acts on is health. The figure turns yellow and then red as
 * it runs out, and blinks through the last seconds, which is the urgency the
 * shrinking bar used to carry.
 */
function drawBars(ctx: CanvasRenderingContext2D, m: YardMeteor, scale: number): void {
  const b = rockBox(m);
  const w = barWidth(b.w);
  const x = Math.round(m.x - w / 2);
  const y = b.y - 6;
  const healthFrac = m.meteor.health / m.meteor.kind.health;
  ctx.fillStyle = OUTLINE;
  ctx.fillRect(x - 1, y - 1, w + 2, 5);
  ctx.fillStyle = HEALTH_BACK;
  ctx.fillRect(x, y, w, 3);
  ctx.fillStyle = HEALTH;
  ctx.fillRect(x, y, Math.round(w * healthFrac), 3);

  const left = m.meteor.secondsLeft;
  // Off for the last sixth of each half-second through the final stretch:
  // enough to read as a blink without the figure being gone half the time.
  if (left <= LAST_SECONDS && left % 0.5 < 0.08) return;
  // Tenths, rounded up like the whole seconds were, so a live meteor never reads 0.0s.
  const text = `${(Math.ceil(left * 10) / 10).toFixed(1)}s`;
  ctx.save();
  ctx.font = `800 ${COUNTDOWN_PX / scale}px 'Baloo 2', system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 4 / scale;
  ctx.strokeStyle = OUTLINE;
  ctx.strokeText(text, m.x, y - 3);
  ctx.fillStyle = left <= LAST_SECONDS ? HEALTH : left <= HURRY_SECONDS ? HURRY : WHITE;
  ctx.fillText(text, m.x, y - 3);
  ctx.restore();
}

function drawFireball(ctx: CanvasRenderingContext2D, x: number, y: number, size: number): void {
  const r = Math.max(4, Math.round(size / 2));
  // Tail up and to the right, then the hot core.
  ctx.fillStyle = FLAME;
  ctx.fillRect(x + r - 2, y - r - 6, 3, 6);
  ctx.fillRect(x + r + 1, y - r - 10, 2, 5);
  ctx.fillStyle = FLAME_HOT;
  ctx.fillRect(x - r, y - r, r * 2, r * 2);
  ctx.fillStyle = OUTLINE;
  ctx.fillRect(x - r - 1, y - r + 1, 1, r * 2 - 2);
  ctx.fillRect(x + r, y - r + 1, 1, r * 2 - 2);
  ctx.fillRect(x - r + 1, y - r - 1, r * 2 - 2, 1);
  ctx.fillRect(x - r + 1, y + r, r * 2 - 2, 1);
  ctx.fillStyle = ROCK_SHADE;
  ctx.fillRect(x - r + 1, y - r + 1, r * 2 - 2, r * 2 - 2);
  ctx.fillStyle = FLAME_TIP;
  ctx.fillRect(x - r + 1, y - r + 1, 2, 2);
}

export const METEOR_FRAGMENT_COLORS: readonly string[] = [ROCK, ROCK_SHADE, ROCK_DARK];
export const METEOR_EMBER_COLORS: readonly string[] = [FLAME, FLAME_HOT, FLAME_TIP];
